// ============================================================================
// sound.ts — звуковые оповещения (SRP/SSoT):
//   - настройки — user_settings (settingsStore): sound.enabled (глобально),
//     sound.chat_enabled / sound.notify_enabled (по событиям),
//     sound.chat_file / sound.notify_file (свой звук — id файла из «Файлы»);
//   - playSound(kind): если выключено — тишина; свой звук (downloadFile → Audio)
//     иначе встроенный (Web Audio — короткий «динь»/«динь-динь», без файлов);
//   - звуки играют ТОЛЬКО по явному действию события (чат/уведомление) —
//     никакого фонового шума.
// ============================================================================

import { settingsStore } from '@/lib/settingsStore';
import { getSDK } from '@/store/sdkStore';
import { fileBytes } from '@/lib/fileBytes';

export type SoundKind = 'chat' | 'notify';

interface SoundSettings {
  enabled: boolean;
  chat: boolean;
  notify: boolean;
  chatFile?: string;
  notifyFile?: string;
}

export function getSoundSettings(): SoundSettings {
  return {
    enabled: settingsStore.getJSON<boolean>('sound.enabled', true) !== false,
    chat: settingsStore.getJSON<boolean>('sound.chat_enabled', true) !== false,
    notify: settingsStore.getJSON<boolean>('sound.notify_enabled', true) !== false,
    chatFile: settingsStore.getJSON<string>('sound.chat_file', '') || undefined,
    notifyFile: settingsStore.getJSON<string>('sound.notify_file', '') || undefined,
  };
}

const SETTINGS_KEYS: Record<SoundKind, string> = {
  chat: 'sound.chat_enabled',
  notify: 'sound.notify_enabled',
};
const FILE_KEYS: Record<SoundKind, string> = {
  chat: 'sound.chat_file',
  notify: 'sound.notify_file',
};

/** Вкл/выкл звука события (и глобально — kind='chat' дублирует глобальный вкл) */
export function setSoundEnabled(kind: SoundKind, enabled: boolean): void {
  settingsStore.setJSON(SETTINGS_KEYS[kind], enabled);
  if (kind === 'chat') settingsStore.setJSON('sound.enabled', enabled);
}

/** Задать свой звук события (id файла; пусто = встроенный) */
export function setSoundFile(kind: SoundKind, fileId: string): void {
  settingsStore.setJSON(FILE_KEYS[kind], fileId);
}

let audioCtx: AudioContext | null = null;

function ctx(): AudioContext | null {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    return audioCtx;
  } catch {
    return null;
  }
}

/** Встроенный звук (Web Audio — «динь» для chat, «динь-динь» для notify) */
function playBuiltin(kind: SoundKind): void {
  const ac = ctx();
  if (!ac) return;
  if (ac.state === 'suspended') void ac.resume();
  const notes = kind === 'chat' ? [880] : [880, 1174];
  const now = ac.currentTime;
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = notes[i];
    const t0 = now + i * 0.12;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
    osc.connect(gain).connect(ac.destination);
    osc.start(t0);
    osc.stop(t0 + 0.4);
  }
}

/** Свой звук (файл из «Файлы») */
async function playFile(fileId: string): Promise<void> {
  const ac = ctx();
  if (!ac) return;
  const r: any = await getSDK().files.downloadFile({ id: fileId });
  const bytes = fileBytes(r?.data);
  if (bytes.length === 0) throw new Error('звуковой файл пуст');
  const buf = await ac.decodeAudioData(bytes.slice().buffer as ArrayBuffer);
  const src = ac.createBufferSource();
  src.buffer = buf;
  const gain = ac.createGain();
  gain.gain.value = 0.9;
  src.connect(gain).connect(ac.destination);
  src.start();
}

/** Проиграть звук события (свой файл или встроенный; выкл — тишина) */
export async function playSound(kind: SoundKind): Promise<void> {
  const st = getSoundSettings();
  if (!st.enabled) return;
  if (kind === 'chat' && !st.chat) return;
  if (kind === 'notify' && !st.notify) return;
  const fileId = kind === 'chat' ? st.chatFile : st.notifyFile;
  if (fileId) {
    try {
      await playFile(fileId);
      return;
    } catch {
      // свой звук недоступен — встроенный (не падаем)
    }
  }
  playBuiltin(kind);
}
